import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useCreateTemplate } from '@/lib/hooks/useCurriculum';

interface CreateCurriculumModalProps {
  open: boolean;
  onClose: () => void;
}

function slugify(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export function CreateCurriculumModal({ open, onClose }: CreateCurriculumModalProps) {
  const navigate = useNavigate();
  const createMutation = useCreateTemplate();
  const [form, setForm] = useState({ title: '', slug: '', description: '' });

  if (!open) return null;

  const handleTitleChange = (title: string) =>
    setForm(f => ({ ...f, title, slug: slugify(title) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.slug.trim()) return;
    const { data } = await createMutation.mutateAsync({
      title: form.title.trim(),
      slug: form.slug.trim(),
      description: form.description.trim() || undefined,
    });
    setForm({ title: '', slug: '', description: '' });
    onClose();
    if (data?.id) {
      navigate({ to: '/admin/curriculum/$templateId', params: { templateId: data.id } });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-base font-semibold">New Curriculum</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="cc-title" className="mb-1 block text-sm font-medium">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="cc-title"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="e.g. MERN Stack Complete"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="cc-slug" className="mb-1 block text-sm font-medium">
              Slug <span className="text-destructive">*</span>
            </label>
            <input
              id="cc-slug"
              value={form.slug}
              onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
              placeholder="auto-generated"
              className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
            />
          </div>
          <div>
            <label htmlFor="cc-desc" className="mb-1 block text-sm font-medium">Description</label>
            <textarea
              id="cc-desc"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={2}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!form.title.trim() || !form.slug.trim() || createMutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating…' : 'Create & Open'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
